import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs';
import { BaseModel } from '../models/basemodel';
import { Pageable } from '../models/pageable';
import { DataTableModuleConfiguration } from './datatable-module.config';
import { IMPERSONATION_SESSION_STORAGE_KEY } from '../utils/constants.impersonation';

// Search, Filter, Sort Options
export interface SFSOptions {
  search: string;
  filter: string[] | null;
  resultFilters?: string[] | null;
  sortColumn: string;
  sortOrder: string;
  pageNumber: number;
  pageSize: number;
  additionalQueryParams?: HttpParams;
  removeDataOnLoad?: boolean;
}

@Injectable({ providedIn: 'root' })
export abstract class AbstractDataService<T extends BaseModel> {
  private _apiEndpoint!: string;

  protected get apiEndpoint(): string {
    return this._apiEndpoint;
  }

  protected set apiEndpoint(value: string) {
    this._apiEndpoint = value;
  }

  protected readonly defaultSFSOptions: Partial<SFSOptions>;

  protected http = inject(HttpClient);
  protected configuration = inject(DataTableModuleConfiguration);
  protected baseUrl: string;

  protected constructor(baseUrl: string,
  ) {
    this.baseUrl = baseUrl;
    this.defaultSFSOptions = {
      search: '',
      filter: [],
      sortColumn: '',
      sortOrder: 'asc',
      pageNumber: 0,
      pageSize: this.configuration.defaultPageSize,
    };
  }

  list(options: Partial<SFSOptions> = {}): Observable<Pageable<T>> {
    const params = this.getParams(options);
    return this.http.get<Pageable<T>>(this.baseUrl + this.apiEndpoint, {
      params,
    }).pipe(map((page) => {
      page.content = page.content.map((item) => this.loadedDataTransform(item));
      return page;
    }));
  }

  getParams(partialOptions: Partial<SFSOptions>): HttpParams {
    const options = {
      ...this.defaultSFSOptions,
      ...partialOptions,
    } as Partial<SFSOptions>; // fields can (still) be undefined
    let params = new HttpParams();
    if (options.search !== undefined && options.search !== '') {
      params = params.append('search', options.search);
    }
    if (options.filter && options.filter.length > 0) {
      options.filter.forEach((f) => {
        params = params.append('filters', f);
      });
    }
    if (options.resultFilters && options.resultFilters.length > 0) {
      options.resultFilters.forEach((f) => {
        params = params.append('resultFilters', f);
      });
    }
    if (options.sortColumn !== undefined && options.sortColumn !== '') {
      // Hot-fix, because Spring does not support property names with _ in them.
      // (e.g. given_name, will be treated as given)
      params = params.append('sort', `${options.sortColumn.replace('_', '')},${options.sortOrder}`);
    }
    if (options.pageNumber !== undefined) {
      params = params.append('page', options.pageNumber.toString());
    }
    if (options.pageSize !== undefined) {
      params = params.append('size', options.pageSize.toString());
    }
    if (options.additionalQueryParams) {
      options.additionalQueryParams.keys().forEach((key) => {
        params = params.append(key, `${options.additionalQueryParams!.get(key)}`);
      });
    }
    return params;
  }

  get(id: string): Observable<T> {
    return this.http.get<T>(`${this.baseUrl + this.apiEndpoint}/${id}`).pipe(map((item) => this.loadedDataTransform(item)));
  }

  createOrUpdate(object: T): Observable<T> {
    if (!object.id) {
      return this.create(object);
    }
    return this.update(object);
  }

  create(object: T): Observable<T> {
    return this.http.post<T>(
      this.baseUrl + this.apiEndpoint,
      object,
      { headers: this.getHeades() },
    ).pipe(map((item) => this.loadedDataTransform(item)));
  }

  update(object: T): Observable<T> {
    return this.http.put<T>(
      `${this.baseUrl + this.apiEndpoint}/${object.id}`,
      object,
      { headers: this.getHeades() },
    ).pipe(map((item) => this.loadedDataTransform(item)));
  }

  delete(object: T): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl + this.apiEndpoint}/${object.id}`);
  }

  protected loadedDataTransform(data: T): T {

    data.createdAt = data.createdAt ? dayjs(data.createdAt) : data.createdAt;
    data.lastUpdatedAt = data.lastUpdatedAt ? dayjs(data.lastUpdatedAt) : data.lastUpdatedAt;
    return data;
  }

  protected getHeades(): HttpHeaders {
    const impersonationDataJson = sessionStorage.getItem(IMPERSONATION_SESSION_STORAGE_KEY);
    if (impersonationDataJson !== null) {
      const impersonationData = JSON.parse(impersonationDataJson);
      return new HttpHeaders({
        'X-ACTING-USER': impersonationData.actor,
      });
    }
    return new HttpHeaders();
  }
}
